import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { MessageRole } from '../../types/debate';
import { motion, AnimatePresence } from 'framer-motion';
import { LogIn, User, Home } from 'lucide-react';

export default function SharedDebateView() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [debate, setDebate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadSharedDebate = async () => {
      const sharedId = searchParams.get('share');
      if (!sharedId) {
        setError('No shared debate ID provided');
        setLoading(false);
        return;
      }

      try {
        const debateDoc = await getDoc(doc(db, 'sharedDebates', sharedId));
        if (!debateDoc.exists()) {
          setError('Shared debate not found');
          setLoading(false);
          return;
        }

        setDebate(debateDoc.data());
        setLoading(false);
      } catch (err) {
        console.error('Error loading shared debate:', err);
        setError('Failed to load shared debate');
        setLoading(false);
      }
    };

    loadSharedDebate();
  }, [searchParams]);

  const getMessageStyle = (role) => {
    switch (role) {
      case MessageRole.USER:
        return 'mx-auto max-w-2xl bg-gradient-to-r from-purple-500 to-pink-600 text-white';
      case MessageRole.PROTAGONIST:
        return 'ml-auto max-w-3xl bg-gradient-to-r from-blue-500 to-cyan-600 text-white';
      case MessageRole.ANTAGONIST:
        return 'mr-auto max-w-3xl bg-gradient-to-r from-red-500 to-orange-600 text-white';
      case MessageRole.EVALUATOR:
        return 'mx-auto w-full max-w-2xl bg-gradient-to-r from-green-500 to-emerald-600 text-white';
      default:
        return 'mx-auto bg-gray-800/50 text-gray-200';
    }
  };

  const MessageIcon = ({ role }) => {
    switch (role) {
      case MessageRole.USER:
        return <div className="bg-purple-500 rounded-full p-2"><User className="w-4 h-4 text-white" /></div>;
      case MessageRole.PROTAGONIST:
        return <img src="/protagonist.png" alt="Protagonist" className="w-8 h-8 rounded-full" />;
      case MessageRole.ANTAGONIST:
        return <img src="/antagonist.png" alt="Antagonist" className="w-8 h-8 rounded-full" />;
      default:
        return null;
    }
  };

  const Header = () => (
    <div className="fixed top-0 left-0 right-0 h-14 bg-gray-900 border-b border-gray-800 z-40 flex items-center px-4">
      <div className="flex items-center gap-2">
        <button
          onClick={() => navigate('/')}
          className="text-xl font-bold bg-gradient-to-r from-purple-400 to-pink-600 text-transparent bg-clip-text"
        >
          Verdyct
        </button>
      </div>
      <div className="flex items-center ml-auto gap-2">
        <button
          onClick={() => navigate('/login')}
          className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-600 text-white rounded-lg hover:opacity-90 transition-opacity"
        >
          <LogIn className="w-4 h-4" />
          <span>Sign in to create debates</span>
        </button>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900">
        <Header />
        <div className="pt-14 flex flex-col items-center justify-center min-h-[calc(100vh-56px)]">
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
            <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
            <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
          </div>
        </div>
      </div>
    );
  }

  if (error || !debate) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900">
        <Header />
        <div className="pt-14 flex flex-col items-center justify-center min-h-[calc(100vh-56px)]">
          <div className="text-red-500 mb-4">{error}</div>
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-600 text-white rounded-lg hover:opacity-90 transition-opacity"
          >
            <Home className="w-4 h-4" />
            <span>Go to Home</span>
          </button>
        </div>
      </div>
    );
  }

  // Helper function to render prompt history
  const renderPromptHistory = () => {
    if (!debate.prompts || debate.prompts.length <= 1) {
      return <h2 className="text-2xl font-bold text-white text-center mb-2">{debate.prompts[0]}</h2>;
    }

    return (
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-white text-center mb-4">Debate History</h2>
        <div className="space-y-4">
          {debate.prompts.map((prompt, index) => (
            <div key={index} className="bg-gray-800/50 rounded-lg p-4">
              <div className="text-sm text-gray-400 mb-1">
                {index === 0 ? 'Initial Question' : `Follow-up Question ${index}`}
              </div>
              <div className="text-white">{prompt}</div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900">
      <Header />
      {/* Main content */}
      <main className="pt-14 mx-auto px-4">
        <div className="py-8">
          {renderPromptHistory()}
          <div className="border-t border-gray-800 pt-8">
            <AnimatePresence mode="popLayout">
              {debate.uiMessages.map((message, index) => (
                <motion.div
                  key={`message-${message.role}-${index}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`mb-6 flex ${
                    message.role === MessageRole.EVALUATOR || message.role === MessageRole.USER
                      ? 'justify-center w-full' 
                      : message.role === MessageRole.ANTAGONIST 
                        ? 'justify-start' 
                        : 'justify-end'
                  }`}
                >
                  <div className={`flex items-end space-x-2 ${
                    message.role === MessageRole.EVALUATOR ? 'w-full max-w-2xl' : ''
                  }`}>
                    {(message.role === MessageRole.ANTAGONIST || message.role === MessageRole.USER) && (
                      <MessageIcon role={message.role} />
                    )}
                    <div
                      className={`rounded-2xl px-6 py-4 ${getMessageStyle(message.role)} ${
                        message.role === MessageRole.EVALUATOR ? 'text-center my-8' : ''
                      }`}
                    >
                      {message.role === MessageRole.EVALUATOR && (
                        <div className="font-semibold mb-2">Final Evaluation</div>
                      )}
                      <p className="whitespace-pre-wrap">{message.content}</p>
                    </div>
                    {message.role === MessageRole.PROTAGONIST && (
                      <MessageIcon role={message.role} />
                    )}
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </div>
      </main>
    </div>
  );
}